import { create } from 'zustand';
import type { ChatMsg } from '../../lib/types';
import { readPref, savePref } from '../../lib/prefs';

// New chat messages in a room: a short chime, and a popup when the chat itself isn't on screen
// (scrolled away, on a phone below the player, or the player is fullscreen).

export const chatSoundOn = () => readPref('chatSound') !== 'off';
export const setChatSound = (on: boolean) => savePref('chatSound', on ? 'on' : 'off');

let audio: AudioContext | null = null;
// Browsers only let a page make sound after a tap or click, so the context is unlocked on the first one.
if (typeof window !== 'undefined') {
  const unlock = () => {
    try {
      audio ??= new AudioContext();
      if (audio.state === 'suspended') audio.resume();
    } catch {
      /* no Web Audio */
    }
  };
  window.addEventListener('pointerdown', unlock, { capture: true, passive: true });
  window.addEventListener('keydown', unlock, { capture: true });
}

/** A soft two-note chime, made on the fly (no sound file to load). */
export function playChime() {
  if (!audio || audio.state !== 'running' || !chatSoundOn()) return;
  const t = audio.currentTime;
  for (const [i, f] of [880, 1320].entries()) {
    const o = audio.createOscillator();
    const g = audio.createGain();
    o.type = 'sine';
    o.frequency.value = f;
    const at = t + i * 0.11;
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.12, at + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.35);
    o.connect(g).connect(audio.destination);
    o.start(at);
    o.stop(at + 0.4);
  }
}

interface Popup {
  id: string;
  name: string;
  text: string;
}
export const usePopups = create<{ items: Popup[] }>(() => ({ items: [] }));
export const dismissChatPopup = (id: string) => usePopups.setState((s) => ({ items: s.items.filter((p) => p.id !== id) }));

/** Is the room's chat list visible right now (and not hidden behind a fullscreen player)? */
function chatVisible() {
  if (document.fullscreenElement || document.visibilityState !== 'visible') return false;
  const el = document.querySelector('.chat-list');
  if (!el) return false;
  const r = el.getBoundingClientRect();
  // Any real part of the list on screen counts: the new message shows up there, so a popup would
  // just repeat it.
  const shown = Math.min(r.bottom, innerHeight) - Math.max(r.top, 0);
  return shown > 40;
}

/** Someone else's message arrived: chime, and pop it up if the chat isn't in view. */
export function notifyChat(m: ChatMsg) {
  playChime();
  if (chatVisible()) return;
  usePopups.setState((s) => ({ items: [...s.items.slice(-2), { id: m.id, name: m.name, text: m.file ? `📎 ${m.file.name}${m.text ? ` · ${m.text}` : ''}` : m.text }] }));
  setTimeout(() => dismissChatPopup(m.id), 6000);
}

export function clearChatPopups() {
  usePopups.setState({ items: [] });
}
